/**
 * Active Countries Configuration
 *
 * This configuration defines which countries are highlighted on the world map.
 * Each country includes ISO 3166-1 alpha-2 codes and localized names.
 *
 * To add a new country:
 * 1. Add a new entry with the country code (lowercase)
 * 2. Provide names in all supported locales (en, de, uk)
 * 3. Set the displayOrder (higher numbers appear first in the tag list)
 */

export interface ActiveCountry {
  countryCode: string;
  countryName: {
    en: string;
    de: string;
    uk: string;
  };
  displayOrder: number;
}

export const ACTIVE_COUNTRIES: ActiveCountry[] = [
  {
    countryCode: "np",
    countryName: {
      en: "Nepal",
      de: "Nepal",
      uk: "Непал",
    },
    displayOrder: 5,
  }, 
  {
    countryCode: "ua",
    countryName: {
      en: "Ukraine",
      de: "Ukraine",
      uk: "Україна",
    },
    displayOrder: 4,
  },
  {
    countryCode: "rw",
    countryName: {
      en: "Rwanda",
      de: "Ruanda",
      uk: "Руанда",
    },
    displayOrder: 4,
  },
  {
    countryCode: "ug",
    countryName: {
      en: "Uganda",
      de: "Uganda",
      uk: "Уганда",
    },
    displayOrder: 4,
  },
  {
    countryCode: "ke",
    countryName: {
      en: "Kenya",
      de: "Kenia",
      uk: "Кенія",
    },
    displayOrder: 4,
  },
  {
    countryCode: "cd",
    countryName: {
      en: "Democratic Republic of the Congo",
      de: "Demokratische Republik Kongo",
      uk: "Демократична Республіка Конго",
    },
    displayOrder: 4,
  },
  {
    countryCode: "kr",
    countryName: {
      en: "South Korea",
      de: "Südkorea",
      uk: "Південна Корея",
    },
    displayOrder: 3,
  },
  {
    countryCode: "jp",
    countryName: {
      en: "Japan",
      de: "Japan",
      uk: "Японія",
    },
    displayOrder: 3,
  },
  {
    countryCode: "pe",
    countryName: {
      en: "Peru",
      de: "Peru",
      uk: "Перу",
    },
    displayOrder: 3,
  },
  // Add Venezuela
  {
    countryCode: "ve",
    countryName: {
      en: "Venezuela",
      de: "Venezuela",
      uk: "Венесуела",
    },
    displayOrder: 3,
  },
  {
    countryCode: "uz",
    countryName: {
      en: "Uzbekistan",
      de: "Usbekistan",
      uk: "Узбекистан",
    },
    displayOrder: 3,
  },
  {
    countryCode: "pt",
    countryName: {
      en: "Portugal",
      de: "Portugal",
      uk: "Португалія",
    },
    displayOrder: 3,
  },
  {
    countryCode: "tr",
    countryName: {
      en: "Turkey",
      de: "Türkei",
      uk: "Туреччина",
    },
    displayOrder: 3,
  },
  {
    countryCode: "my",
    countryName: {
      en: "Malaysia",
      de: "Malaysia",
      uk: "Малайзія",
    },
    displayOrder: 3,
  },
  {
    countryCode: "ro",
    countryName: {
      en: "Romania",
      de: "Rumänien",
      uk: "Румунія",
    },
    displayOrder: 3,
  },
  {
    countryCode: "cl",
    countryName: {
      en: "Chile",
      de: "Chile",
      uk: "Чилі",
    },
    displayOrder: 3,
  },
  {
    countryCode: "ar",
    countryName: {
      en: "Argentina",
      de: "Argentinien",
      uk: "Аргентина",
    },
    displayOrder: 3,
  },
  {
    countryCode: "za",
    countryName: {
      en: "South Africa",
      de: "Südafrika",
      uk: "Південно-Африканська Республіка",
    },
    displayOrder: 3,
  },
  // Add China - Mainland
  {
    countryCode: "cn",
    countryName: {
      en: "China",
      de: "China",
      uk: "Китай",
    },
    displayOrder: 3,
  },
  // Add Tibet (as distinct region)
  {
    countryCode: "tb",
    countryName: {
      en: "Tibet",
      de: "Tibet",
      uk: "Тибет",
    },
    displayOrder: 3,
  },
  {
    countryCode: "id",
    countryName: {
      en: "Indonesia",
      de: "Indonesien",
      uk: "Індонезія",
    },
    displayOrder: 3,
  },
  {
    countryCode: "vn",
    countryName: {
      en: "Vietnam",
      de: "Vietnam",
      uk: "Вʼєтнам",
    },
    displayOrder: 3,
  },
  {
    countryCode: "la",
    countryName: {
      en: "Laos",
      de: "Laos",
      uk: "Лаос",
    },
    displayOrder: 3,
  },
  {
    countryCode: "kh",
    countryName: {
      en: "Cambodia",
      de: "Kambodscha",
      uk: "Камбоджа",
    },
    displayOrder: 3,
  },
  {
    countryCode: "bt",
    countryName: {
      en: "Bhutan",
      de: "Bhutan",
      uk: "Бутан",
    },
    displayOrder: 1,
  },
  {
    countryCode: "ge",
    countryName: {
      en: "Georgia",
      de: "Georgien",
      uk: "Грузія",
    },
    displayOrder: 0,
  },
];

/**
 * Helper function to get a country by its code
 */
export function getCountryByCode(code: string): ActiveCountry | undefined {
  return ACTIVE_COUNTRIES.find(
    (country) => country.countryCode.toLowerCase() === code.toLowerCase()
  );
}

/**
 * Helper function to check if a country is active
 */
export function isCountryActive(code: string): boolean {
  return ACTIVE_COUNTRIES.some(
    (country) => country.countryCode.toLowerCase() === code.toLowerCase()
  );
}

/**
 * Helper function to get all active country codes
 */
export function getActiveCountryCodes(): string[] {
  return ACTIVE_COUNTRIES.map((country) => country.countryCode.toLowerCase());
}

/**
 * Helper function to get countries sorted by display order
 */
export function getSortedCountries(): ActiveCountry[] {
  return [...ACTIVE_COUNTRIES].sort((a, b) => b.displayOrder - a.displayOrder);
}
